<template>
  <div>
    <section class="hero" :style="heroStyle">
      <div class="container hero-grid">
        <div class="hero-content">
          <p class="eyebrow">Bergier Assurances</p>
          <h1>
            Des garanties solides, un accompagnement humain, des décisions claires.
          </h1>
          <p class="lead">
            Nous construisons avec vous une protection sur mesure pour votre famille, votre
            entreprise et votre patrimoine. Un courtier disponible, transparent et engagé à vos côtés.
          </p>
          <div class="hero-actions">
            <router-link class="button primary" to="/contact">Parler à un conseiller</router-link>
            <router-link class="button ghost" to="/entreprises">Découvrir nos offres</router-link>
          </div>
          <div class="hero-metrics">
            <div>
              <p class="metric-value">+20</p>
              <p class="metric-label">Années d’expérience</p>
            </div>
            <div>
              <p class="metric-value">100%</p>
              <p class="metric-label">Conseil indépendant</p>
            </div>
            <div>
              <p class="metric-value">24/7</p>
              <p class="metric-label">Assistance en cas de sinistre</p>
            </div>
          </div>
        </div>
        <div class="hero-card">
          <h2>Ce que nous couvrons</h2>
          <ul>
            <li>Habitation & responsabilité civile</li>
            <li>Automobile & mobilité</li>
            <li>Santé, prévoyance & retraite</li>
            <li>Professionnels & entreprises</li>
            <li>Assurance prêt & patrimoine</li>
            <li>Objets d’art & biens d’exception</li>
          </ul>
          <div class="hero-card-footer">
            <p>Un audit gratuit de vos garanties actuelles.</p>
            <router-link to="/contact">Demander l’audit</router-link>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-header">
          <h2>Nos expertises</h2>
          <p>
            Des solutions claires et adaptées, sélectionnées parmi des assureurs de référence,
            avec une négociation menée dans votre intérêt.
          </p>
        </div>
        <div class="grid three">
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-9.5Z" />
              </svg>
            </div>
            <h3>Assurance dommages</h3>
            <p>
              Protection de votre logement, de vos véhicules et de vos actifs professionnels avec
              des garanties personnalisées et des franchises maîtrisées.
            </p>
            <span class="pill">Habitation · Auto · Entreprise</span>
          </article>
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 3l7 4v5c0 4.4-3 7.7-7 9-4-1.3-7-4.6-7-9V7l7-4Z" />
              </svg>
            </div>
            <h3>Protection des personnes</h3>
            <p>
              Couverture santé, prévoyance et invalidité pour sécuriser votre famille et maintenir
              votre niveau de vie en toutes circonstances.
            </p>
            <span class="pill">Santé · Prévoyance · Retraite</span>
          </article>
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M4 19V5a2 2 0 0 1 2-2h8l6 6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
                <path d="M14 3v6h6" />
              </svg>
            </div>
            <h3>Assurance fine art</h3>
            <p>
              Des garanties dédiées aux œuvres d’art, objets précieux et collections,
              avec un accompagnement expert pour la valorisation et la gestion des sinistres.
            </p>
            <span class="pill">Collections · Œuvres · Patrimoine</span>
          </article>
        </div>
      </div>
    </section>

    <section class="section alt">
      <div class="container">
        <div class="section-header">
          <h2>Parcours dédiés</h2>
          <p>Choisissez un parcours clair selon votre profil.</p>
        </div>
        <div class="grid three">
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M3 20h18" />
                <path d="M6 20V8l6-3 6 3v12" />
                <path d="M9 20v-6h6v6" />
              </svg>
            </div>
            <h3>Entreprises</h3>
            <p>Protection de l’activité, des dirigeants et des équipes.</p>
            <router-link class="text-link" to="/entreprises">Voir la page dédiée →</router-link>
          </article>
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" />
                <path d="M4 20a8 8 0 0 1 16 0" />
              </svg>
            </div>
            <h3>Particuliers</h3>
            <p>Garanties essentielles pour votre quotidien et votre famille.</p>
            <router-link class="text-link" to="/particuliers">Voir la page dédiée →</router-link>
          </article>
          <article class="card">
            <div class="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
                <path d="M7 7l10 10" />
                <path d="M17 7l-10 10" />
              </svg>
            </div>
            <h3>Fine Art</h3>
            <p>Couvertures spécifiques pour objets d’exception.</p>
            <router-link class="text-link" to="/fine-art">Voir la page dédiée →</router-link>
          </article>
        </div>
      </div>
    </section>

    <section class="section alt">
      <div class="container method-grid">
        <div>
          <h2>Notre méthode</h2>
          <p>
            Nous simplifions les démarches et vous apportons une vision précise de vos risques.
            Chaque étape est documentée et expliquée.
          </p>
        </div>
        <div class="steps">
          <div class="step">
            <span>1</span>
            <div>
              <h3>Diagnostic complet</h3>
              <p>Analyse de vos contrats existants, de vos priorités et des zones de fragilité.</p>
            </div>
          </div>
          <div class="step">
            <span>2</span>
            <div>
              <h3>Comparatif et négociation</h3>
              <p>Nous sélectionnons les meilleures options et négocions les garanties.</p>
            </div>
          </div>
          <div class="step">
            <span>3</span>
            <div>
              <h3>Suivi continu</h3>
              <p>Réajustement annuel, assistance en cas de sinistre et interlocuteur dédié.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <div class="section-header">
          <h2>Nos engagements</h2>
          <p>Des principes concrets pour garantir une relation durable et sereine.</p>
        </div>
        <div class="grid four">
          <div class="stat">
            <h3>Transparence</h3>
            <p>Des explications simples, des garanties compréhensibles.</p>
          </div>
          <div class="stat">
            <h3>Disponibilité</h3>
            <p>Un interlocuteur identifié, joignable rapidement.</p>
          </div>
          <div class="stat">
            <h3>Performance</h3>
            <p>Optimisation des coûts et amélioration continue des couvertures.</p>
          </div>
          <div class="stat">
            <h3>Réactivité</h3>
            <p>Gestion prioritaire des sinistres et suivi rigoureux.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  computed: {
    heroStyle() {
      const baseUrl = process.env.BASE_URL || '/';
      return {
        backgroundImage: `linear-gradient(140deg, rgba(247, 249, 255, 0.65) 0%, rgba(238, 243, 255, 0.62) 40%, rgba(255, 255, 255, 0.68) 100%), url(${baseUrl}background3.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
  }
};
</script>
